import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ButtonV2, Combobox, DialogV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import type { ComboboxOption } from '@withjoy/joykit/components/Combobox/Combobox.types';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';

// temporary until currency selector and cash fund internationalization
const COUNTRY_OPTIONS: ReadonlyArray<ComboboxOption> = [{ value: 'USA', label: 'United States' }];

type InitHostFormProps = Readonly<{
  onSubmit: (firstName: string, lastName: string) => Promise<void>;
  loading: boolean;
}>;

type InitHostFormValues = Readonly<{
  firstName: string;
  lastName: string;
  countryCode: string;
}>;

export const InitHostForm: React.FC<InitHostFormProps> = ({ onSubmit, loading }) => {
  const { tq } = useTranslation('yourRegistry');
  const { currentUser } = useAuth();
  const telemetry = useCreditCardSetupTelemetry();

  useMount(() => {
    telemetry.creditCardSetupDialog.enter('initHostForm');
  });

  const validationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string().required(tq`First name is required`),
        lastName: Yup.string().required(tq`Last name is required`),
        countryCode: Yup.string().required(tq`Country is required`),
      }),
    [tq],
  );

  const formik = useFormik<InitHostFormValues>({
    initialValues: {
      firstName: currentUser.profile?.firstName || '',
      lastName: currentUser.profile?.lastName || '',
      // temporary, until implement currency selector instead
      countryCode: 'USA',
    },
    validationSchema,
    onSubmit: async values => {
      telemetry.initHostFormSubmitted();
      await onSubmit(values.firstName, values.lastName);
    },
  });

  return (
    <DialogV2.Body>
      <form onSubmit={formik.handleSubmit}>
        <Flex
          flexDirection="column"
          gap={6}
          padding={6}
        >
          <TextV2
            typographyVariant="hed4"
            textAlign="center"
          >
            {tq`Who is your cash fund contact?`}
          </TextV2>
          <FormControl
            label={tq`First Name`}
            isInvalid={formik.touched.firstName && !!formik.errors.firstName}
            error={formik.errors.firstName}
            overrides={{
              Label: {
                props: {
                  typographyVariant: 'label3',
                },
              },
            }}
          >
            <InputV2
              name="firstName"
              placeholder={tq`First Name`}
              value={formik.values.firstName}
              onChange={e => formik.setFieldValue('firstName', e.target.value)}
              onBlur={formik.handleBlur}
            />
          </FormControl>
          <FormControl
            label={tq`Last Name`}
            isInvalid={formik.touched.lastName && !!formik.errors.lastName}
            error={formik.errors.lastName}
            overrides={{
              Label: {
                props: {
                  typographyVariant: 'label3',
                },
              },
            }}
          >
            <InputV2
              name="lastName"
              placeholder={tq`Last Name`}
              value={formik.values.lastName}
              onChange={e => formik.setFieldValue('lastName', e.target.value)}
              onBlur={formik.handleBlur}
            />
          </FormControl>
          <FormControl
            label={tq`Country/Region`}
            isInvalid={formik.touched.countryCode && !!formik.errors.countryCode}
            error={formik.errors.countryCode}
            overrides={{
              Label: {
                props: {
                  typographyVariant: 'label3',
                },
              },
            }}
          >
            <Combobox
              options={COUNTRY_OPTIONS}
              value={formik.values.countryCode}
              onValueChange={value => formik.setFieldValue('countryCode', value)}
              searchable={false}
            />
          </FormControl>
          <Flex
            justifyContent="flex-end"
            gap={3}
            marginTop={4}
          >
            <ButtonV2
              type="submit"
              intent="neutral"
              shape="rounded"
              fullWidth
              loading={formik.isSubmitting || loading}
            >
              {tq`Save`}
            </ButtonV2>
          </Flex>
          <Flex
            marginTop={4}
            justifyContent="center"
          >
            <TextV2
              as="a"
              href="https://stripe.com/legal/connect-account"
              target="_blank"
              rel="noopener noreferrer"
              color="mono8"
              typographyVariant="label2"
              textAlign="center"
              textDecoration="underline"
            >
              {tq`By continuing, you agree to Stripe's terms of service.`}
            </TextV2>
          </Flex>
        </Flex>
      </form>
    </DialogV2.Body>
  );
};
